import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import MobileModal from './MobileModal';
import { PrognosticTableCells, PROGNOSTIC_HEADERS } from './PrognosticMetrics';
import { GoalPercentPicker } from './KorzoTargetPanel';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Format seconds to MM:SS.s
function formatTime(totalSeconds) {
  if (!totalSeconds) return 'N/A';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(1);
  return `${minutes}:${seconds.padStart(4, '0')}`;
}

// Parse time string (MM:SS.s or M:SS.s) to seconds
function parseTime(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length !== 2) return null;
  const minutes = parseInt(parts[0], 10);
  const seconds = parseFloat(parts[1]);
  if (isNaN(minutes) || isNaN(seconds)) return null;
  return minutes * 60 + seconds;
}

// Format template piece description (distance or time)
function formatPieceDescription(template) {
  const pieceType = template.piece_type || 'distance';
  
  if (pieceType === 'distance' && template.distance_meters) {
    return `${template.distance_meters}m`;
  } else if (pieceType === 'time' && template.time_seconds) {
    const mins = Math.floor(template.time_seconds / 60);
    return `${mins}'`;
  }
  return '';
}

// Format interval description
function formatIntervalDescription(template) {
  const intervalType = template.interval_type || 'distance';
  const intervalCount = template.interval_count || 1;
  
  // Check for variable intervals first
  if (template.intervals && template.intervals.length > 0) {
    let intervals = template.intervals;
    if (typeof intervals === 'string') {
      try { intervals = JSON.parse(intervals); } catch { return ''; }
    }
    if (intervalType === 'time') {
      return intervals.map(i => {
        const mins = Math.floor((i.time_seconds || 0) / 60);
        const secs = (i.time_seconds || 0) % 60;
        return secs > 0 ? `${mins}'${secs}"` : `${mins}'`;
      }).join(' / ');
    } else {
      return intervals.map(i => `${i.distance_meters}m`).join(' / ');
    }
  }
  
  // Uniform intervals
  if (intervalCount <= 1) return '';
  
  if (intervalType === 'time' && template.interval_time_seconds) {
    const mins = Math.floor(template.interval_time_seconds / 60);
    return `${intervalCount}x${mins}'`;
  } else if (template.interval_distance_meters) {
    return `${intervalCount}x${template.interval_distance_meters}m`;
  }
  
  return `${intervalCount} intervals`;
}

function parseIntervalDetails(intervalDetails) {
  if (!intervalDetails) return [];
  if (Array.isArray(intervalDetails)) return intervalDetails;
  if (typeof intervalDetails === 'string') {
    try {
      const parsed = JSON.parse(intervalDetails);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatEnvironmentalConditions(source) {
  if (!source) return [];
  const lines = [];
  if (source.wind_speed_kts !== null && source.wind_speed_kts !== undefined && source.wind_speed_kts !== '') {
    lines.push(`Wind: ${source.wind_speed_kts} kts`);
  }
  if (source.wind_direction) lines.push(`Direction: ${source.wind_direction}`);
  if (source.flow_rate !== null && source.flow_rate !== undefined && source.flow_rate !== '') {
    lines.push(`Flow: ${source.flow_rate} cfs`);
  }
  if (source.water_temperature_c !== null && source.water_temperature_c !== undefined && source.water_temperature_c !== '') {
    lines.push(`Water Temp: ${source.water_temperature_c}°C`);
  }
  if (source.time_of_day) lines.push(`Time of Day: ${source.time_of_day}`);
  return lines;
}

const RESULT_TYPES = [
  { value: 'workout', label: 'Workout' },
  { value: 'test', label: 'Test' },
  { value: 'race', label: 'Race' },
  { value: 'other', label: 'Other' }
];

const COMMON_DISTANCES = [
  { value: 2000, label: '2000m' },
  { value: 5000, label: '5000m' },
  { value: 6000, label: '6000m' },
  { value: 10000, label: '10000m' },
  { value: 1000, label: '1000m' },
  { value: 500, label: '500m' }
];

const COMMON_DURATIONS = [
  { value: 1800, label: "30'" },
  { value: 1200, label: "20'" },
  { value: 900, label: "15'" },
  { value: 600, label: "10'" },
  { value: 300, label: "5'" },
  { value: 60, label: "1'" }
];

const PIECE_TYPES = [
  { value: 'distance', label: 'Distance (measure time)', description: 'Athletes row a set distance, submit their time' },
  { value: 'time', label: 'Time (measure distance)', description: 'Athletes row for a set time, submit distance covered' }
];

const INTERVAL_TYPES = [
  { value: 'none', label: 'Single Piece' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'variable', label: 'Variable' }
];

export default function Reports() {
  const { token } = useAuth();
  const [expandedRows, setExpandedRows] = useState({});
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateReport, setTemplateReport] = useState(null);
  
  // Reports state
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Teams for selection
  const [availableTeams, setAvailableTeams] = useState([]);
  
  // General state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Template form state
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templatePieceType, setTemplatePieceType] = useState('distance');
  const [templateDistance, setTemplateDistance] = useState('');
  const [templateDistanceCustom, setTemplateDistanceCustom] = useState('');
  const [templateTimeSeconds, setTemplateTimeSeconds] = useState('');
  const [templateTimeCustom, setTemplateTimeCustom] = useState('');
  const [templateResultType, setTemplateResultType] = useState('test');
  const [templateIntervalMode, setTemplateIntervalMode] = useState('none');
  const [templateIntervalCount, setTemplateIntervalCount] = useState(1);
  const [templateIntervalDistance, setTemplateIntervalDistance] = useState('');
  const [templateIntervalDistanceCustom, setTemplateIntervalDistanceCustom] = useState('');
  const [templateIntervalTime, setTemplateIntervalTime] = useState('');
  const [templateIntervalTimeCustom, setTemplateIntervalTimeCustom] = useState('');
  const [templateIntervalRest, setTemplateIntervalRest] = useState('');
  const [templateVariableIntervals, setTemplateVariableIntervals] = useState([{ time_seconds: '', distance_meters: '', rest_seconds: '' }]);
  const [templateTeam, setTemplateTeam] = useState('');
  const [templateDueDate, setTemplateDueDate] = useState('');
  const [templateSendNotification, setTemplateSendNotification] = useState(true);
  const [zoneLoggingEnabled, setZoneLoggingEnabled] = useState(false);
  const [zoneDefinitions, setZoneDefinitions] = useState([]);
  const [targetTimesEnabled, setTargetTimesEnabled] = useState(false);
  const [templateIntendedZones, setTemplateIntendedZones] = useState({});
  const [templateWindSpeed, setTemplateWindSpeed] = useState('');
  const [templateWindDirection, setTemplateWindDirection] = useState('');
  const [templateFlowRate, setTemplateFlowRate] = useState('');
  const [templateWaterTemperature, setTemplateWaterTemperature] = useState('');
  const [templateTimeOfDay, setTemplateTimeOfDay] = useState('');
  const [fillingConditions, setFillingConditions] = useState(false);
  const [conditionsFillMessage, setConditionsFillMessage] = useState('');
  const [conditionsAtLocal, setConditionsAtLocal] = useState('');
  const [templateGoalPercent, setTemplateGoalPercent] = useState('');
  const intervalInputMode = templatePieceType === 'time' ? 'time' : 'distance';

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchTeams(), fetchZoneSettings(), fetchTemplates(false), fetchReports(false)]);
      setLoading(false);
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTeams = async () => {
    try {
      // Fetch unique teams from athletes
      const res = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (res.ok) {
        const athletes = await res.json();
        const teams = [...new Set(athletes.filter(a => a.team).map(a => a.team))].sort();
        setAvailableTeams(teams);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const fetchZoneSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams/settings`, {
        headers: getAuthHeaders(token)
      });
      if (res.ok) {
        const data = await res.json();
        setZoneLoggingEnabled(Boolean(data?.settings?.zoneLoggingEnabled));
        setZoneDefinitions(Array.isArray(data?.settings?.zoneDefinitions) ? data.settings.zoneDefinitions : []);
        setTargetTimesEnabled(Boolean(data?.settings?.targetTimesEnabled));
      }
    } catch (err) {
      console.error('Error fetching zone settings:', err);
    }
  };

  const buildTemplateZonePayload = () => {
    const payload = {};
    zoneDefinitions.forEach(zone => {
      const raw = templateIntendedZones?.[zone.key];
      if (raw !== undefined && raw !== null && raw !== '') {
        const parsed = parseInt(raw, 10);
        if (!isNaN(parsed) && parsed >= 0) {
          payload[zone.key] = parsed;
        }
      }
    });
    return Object.keys(payload).length > 0 ? payload : null;
  };

  const fetchTemplates = async (manageLoading = true) => {
    try {
      if (manageLoading) setLoading(true);
      const res = await fetch(`${API_URL}/api/result-templates`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to fetch templates');
      const data = await res.json();
      setTemplates(data);
    } catch (err) {
      setError(err.message);
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchReports = async (manageLoading = true) => {
    try {
      if (manageLoading) setLoading(true);
      const res = await fetch(`${API_URL}/api/reports`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to fetch reports');
      const data = await res.json();
      setReports(data);
    } catch (err) {
      setError(err.message);
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchTemplateReport = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/result-templates/${templateId}/report`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to fetch template report');
      const data = await res.json();
      setTemplateReport(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchReportDetails = async (reportId) => {
    try {
      const res = await fetch(`${API_URL}/api/reports/${reportId}`, {
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to fetch report');
      const data = await res.json();
      setSelectedReport(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Template handlers
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Convert local datetime to ISO string with timezone info
    // datetime-local gives us "2024-01-15T14:30" in local time
    // We need to convert to full ISO string so the server stores it correctly
    let dueDateISO = null;
    if (templateDueDate) {
      const localDate = new Date(templateDueDate);
      dueDateISO = localDate.toISOString();
    }

    // Build the payload based on piece type and interval mode
    const payload = {
      name: templateName,
      description: templateDescription,
      pieceType: templatePieceType,
      resultType: templateResultType,
      team: templateTeam || null,
      dueDate: dueDateISO,
      sendNotification: templateSendNotification,
      intendedZones: zoneLoggingEnabled ? buildTemplateZonePayload() : null,
      windSpeed: templateWindSpeed ? parseFloat(templateWindSpeed) : null,
      windDirection: templateWindDirection || null,
      flowRate: templateFlowRate ? parseFloat(templateFlowRate) : null,
      waterTemperature: templateWaterTemperature ? parseFloat(templateWaterTemperature) : null,
      timeOfDay: templateTimeOfDay || null,
      goalPercent: templateGoalPercent ? parseFloat(templateGoalPercent) : null
    };

    // Set distance or time based on piece type
    // For variable intervals, calculate total from intervals; for others, use the main field
    if (templateIntervalMode === 'variable') {
      // For variable intervals, total will be calculated from intervals
      // Set a placeholder that will be calculated/overwritten
      if (templatePieceType === 'distance') {
        const totalDistance = templateVariableIntervals
          .filter(i => i.distance_meters)
          .reduce((sum, i) => sum + parseInt(i.distance_meters || 0), 0);
        payload.distanceMeters = totalDistance || null;
      } else {
        const totalTime = templateVariableIntervals
          .filter(i => i.time_seconds)
          .reduce((sum, i) => sum + parseInt(i.time_seconds || 0), 0);
        payload.timeSeconds = totalTime || null;
      }
    } else if (templatePieceType === 'distance') {
      const distanceValue = templateDistance === 'custom' ? templateDistanceCustom : templateDistance;
      payload.distanceMeters = parseInt(distanceValue);
    } else {
      const timeValue = templateTimeSeconds === 'custom' ? templateTimeCustom : templateTimeSeconds;
      payload.timeSeconds = parseInt(timeValue);
    }

    // Set interval configuration
    if (templateIntervalMode === 'none') {
      payload.intervalCount = 1;
    } else if (templateIntervalMode === 'fixed') {
      payload.intervalType = templatePieceType;
      payload.intervalCount = parseInt(templateIntervalCount) || 1;
      payload.intervalRestSeconds = templateIntervalRest ? parseTime(templateIntervalRest) : null;
      if (templatePieceType === 'distance') {
        const intervalDistValue = templateIntervalDistance === 'custom' ? templateIntervalDistanceCustom : templateIntervalDistance;
        payload.intervalDistanceMeters = parseInt(intervalDistValue);
      } else {
        const intervalTimeValue = templateIntervalTime === 'custom' ? templateIntervalTimeCustom : templateIntervalTime;
        payload.intervalTimeSeconds = parseInt(intervalTimeValue);
      }
    } else if (templateIntervalMode === 'variable') {
      // Variable intervals - filter out empty ones and build the array
      payload.intervalType = intervalInputMode;
      payload.intervals = templateVariableIntervals
        .filter(i => intervalInputMode === 'time' ? i.time_seconds : i.distance_meters)
        .map(i => ({
          time_seconds: intervalInputMode === 'time' && i.time_seconds ? parseInt(i.time_seconds) : undefined,
          distance_meters: intervalInputMode === 'distance' && i.distance_meters ? parseInt(i.distance_meters) : undefined,
          rest_seconds: i.rest_seconds ? parseTime(i.rest_seconds) : undefined
        }));
      payload.intervalCount = payload.intervals.length;
    }

    try {
      const res = await fetch(`${API_URL}/api/result-templates`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create template');
      }

      setSuccess('Template created! ' + (templateSendNotification ? 'Notifications sent to athletes.' : ''));
      setShowTemplateForm(false);
      resetTemplateForm();
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendReminder = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/result-templates/${templateId}/notify`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reminderOnly: true })
      });

      if (!res.ok) throw new Error('Failed to send reminders');
      const data = await res.json();
      setSuccess(`Reminders sent to ${data.count} athletes`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const res = await fetch(`${API_URL}/api/result-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (!res.ok) throw new Error('Failed to delete template');
      setSuccess('Template deleted');
      fetchTemplates();
      setSelectedTemplate(null);
      setTemplateReport(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateReportFromTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/reports/from-template/${templateId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create report');
      }

      setSuccess('Report created from template submissions');
      fetchReports();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetTemplateForm = () => {
    setTemplateName('');
    setTemplateDescription('');
    setTemplatePieceType('distance');
    setTemplateDistance('');
    setTemplateDistanceCustom('');
    setTemplateTimeSeconds('');
    setTemplateTimeCustom('');
    setTemplateResultType('test');
    setTemplateIntervalMode('none');
    setTemplateIntervalCount(1);
    setTemplateIntervalDistance('');
    setTemplateIntervalDistanceCustom('');
    setTemplateIntervalTime('');
    setTemplateIntervalTimeCustom('');
    setTemplateIntervalRest('');
    setTemplateVariableIntervals([{ time_seconds: '', distance_meters: '', rest_seconds: '' }]);
    setTemplateTeam('');
    setTemplateDueDate('');
    setTemplateSendNotification(true);
    setTemplateIntendedZones({});
    setTemplateWindSpeed('');
    setTemplateWindDirection('');
    setTemplateFlowRate('');
    setTemplateWaterTemperature('');
    setTemplateTimeOfDay('');
    setConditionsFillMessage('');
    setConditionsAtLocal('');
    setTemplateGoalPercent('');
  };

  const applyConditionsPayload = (data, { live } = {}) => {
    if (data.weather?.windSpeedKts != null) {
      setTemplateWindSpeed(String(data.weather.windSpeedKts));
    }
    if (data.weather?.windDirection) {
      setTemplateWindDirection(data.weather.windDirection);
    }
    if (data.water?.dischargeCfs != null) {
      setTemplateFlowRate(String(data.water.dischargeCfs));
    }
    if (data.water?.temperatureC != null) {
      setTemplateWaterTemperature(String(data.water.temperatureC));
    }
    if (data.timeOfDay) {
      setTemplateTimeOfDay(data.timeOfDay);
    }

    const missing = [];
    if (!data.weather) missing.push('weather');
    if (!data.water) missing.push('USGS water');
    const when = data.requestedAt
      ? new Date(data.requestedAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
      : null;
    if (missing.length) {
      setConditionsFillMessage(`Filled available fields${when ? ` for ${when}` : ''}. Missing: ${missing.join(', ')}.`);
    } else if (live) {
      setConditionsFillMessage('Filled from live weather and USGS water data.');
    } else {
      setConditionsFillMessage(`Filled from weather and USGS water data${when ? ` at ${when}` : ''}.`);
    }
  };

  const fetchConditionsForFill = async (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) search.set(key, value);
    });
    const query = search.toString();
    const res = await fetch(`${API_URL}/api/conditions${query ? `?${query}` : ''}`, {
      headers: getAuthHeaders(token)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to load conditions');
    }
    return data;
  };

  const fillFromLiveConditions = async () => {
    setFillingConditions(true);
    setConditionsFillMessage('');
    try {
      const data = await fetchConditionsForFill();
      applyConditionsPayload(data, { live: true });
    } catch (err) {
      setConditionsFillMessage(err.message);
    } finally {
      setFillingConditions(false);
    }
  };

  const fillFromConditionsAt = async () => {
    if (!conditionsAtLocal) {
      setConditionsFillMessage('Pick a date and time first.');
      return;
    }
    setFillingConditions(true);
    setConditionsFillMessage('');
    try {
      const at = new Date(conditionsAtLocal);
      if (Number.isNaN(at.getTime())) {
        throw new Error('That date/time is not valid');
      }
      const data = await fetchConditionsForFill({ datetime: at.toISOString() });
      applyConditionsPayload(data, { live: false });
    } catch (err) {
      setConditionsFillMessage(err.message);
    } finally {
      setFillingConditions(false);
    }
  };

  // Helper to add/remove variable intervals
  const addVariableInterval = () => {
    setTemplateVariableIntervals([...templateVariableIntervals, { time_seconds: '', distance_meters: '', rest_seconds: '' }]);
  };

  const removeVariableInterval = (index) => {
    if (templateVariableIntervals.length > 1) {
      setTemplateVariableIntervals(templateVariableIntervals.filter((_, i) => i !== index));
    }
  };

  const updateVariableInterval = (index, field, value) => {
    const updated = [...templateVariableIntervals];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateVariableIntervals(updated);
  };

  // Report handlers
  const handleShareReport = async (reportId, shared) => {
    try {
      const res = await fetch(`${API_URL}/api/reports/${reportId}/share`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ shared })
      });

      if (!res.ok) throw new Error('Failed to update sharing');
      setSuccess(shared ? 'Report shared with team' : 'Report unshared');
      fetchReports();
      if (selectedReport?.id === reportId) {
        fetchReportDetails(reportId);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report?')) return;

    try {
      const res = await fetch(`${API_URL}/api/reports/${reportId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (!res.ok) throw new Error('Failed to delete report');
      setSuccess('Report deleted');
      fetchReports();
      setSelectedReport(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Templates</h1>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="space-y-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <h2 className="text-lg font-semibold text-theme-primary">Result Templates</h2>
          </div>
          {/* Create Template Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowTemplateForm(true)}
              className="px-4 py-2 bg-theme-primary text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              + Create Template
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No templates yet. Create one to request results from your athletes.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map(template => {
                const pieceDesc = formatPieceDescription(template);
                const intervalDesc = formatIntervalDescription(template);
                const isTimeBased = template.piece_type === 'time';
                const environmentalConditions = formatEnvironmentalConditions(template);
                
                return (
                  <div
                    key={template.id}
                    className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedTemplate?.id === template.id ? 'ring-2 ring-theme-primary' : ''
                    }`}
                    onClick={() => {
                      setSelectedTemplate(template);
                      fetchTemplateReport(template.id);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <p className="text-sm text-gray-500">
                          {intervalDesc ? `${intervalDesc} • ` : ''}{pieceDesc}
                          {isTimeBased && ' (timed)'}
                          {template.team && ` • ${template.team}`}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {template.is_active ? 'Active' : 'Closed'}
                      </span>
                    </div>
                    
                    {template.description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{template.description}</p>
                    )}

                    {template.goal_percent != null && (
                      <p className="text-xs text-indigo-700 mt-2 font-medium">
                        Goal: {Number(template.goal_percent).toFixed(1)}% of 2k
                      </p>
                    )}

                    {environmentalConditions.length > 0 && (
                      <p className="text-xs text-teal-700 mt-2">
                        Conditions: {environmentalConditions.join(' • ')}
                      </p>
                    )}
                    
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {template.submission_count || 0} / {template.target_count || '?'} submitted
                      </span>
                      {template.due_date && (
                        <span className="text-orange-600">
                          Due: {new Date(template.due_date).toLocaleString(undefined, { 
                            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Template Detail / Report */}
          {selectedTemplate && templateReport && (
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{templateReport.template.name}</h2>
                  <p className="text-gray-500">
                    {formatIntervalDescription(templateReport.template) ? `${formatIntervalDescription(templateReport.template)} • ` : ''}
                    {formatPieceDescription(templateReport.template)}
                    {templateReport.template.piece_type === 'time' && ' (timed)'}
                    {templateReport.template.team && ` • ${templateReport.template.team}`}
                    {' • '}{templateReport.stats.completionRate}% complete
                    {templateReport.template.goal_percent != null && (
                      <> • Goal {Number(templateReport.template.goal_percent).toFixed(1)}% of 2k</>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSendReminder(selectedTemplate.id)}
                    className="px-3 py-1.5 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200"
                  >
                    Send Reminder
                  </button>
                  <button
                    onClick={() => handleCreateReportFromTemplate(selectedTemplate.id)}
                    className="px-3 py-1.5 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200"
                    disabled={templateReport.submissions.length === 0}
                  >
                    Create Report
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                    className="px-3 py-1.5 text-sm bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {formatEnvironmentalConditions(templateReport.template).length > 0 && (
                <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 text-sm text-teal-900">
                  <span className="font-semibold">Environmental Conditions: </span>
                  {formatEnvironmentalConditions(templateReport.template).join(' • ')}
                </div>
              )}

              {/* Submissions Table */}
              {templateReport.submissions.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Rank</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Athlete</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Boat</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Time</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Split</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">CST%</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">SR</th>
                        {PROGNOSTIC_HEADERS.map((h) => (
                          <th key={h.key} className="px-4 py-2 text-right font-medium text-gray-600">{h.label}</th>
                        ))}
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Pieces</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {templateReport.submissions.map((sub, idx) => {
                        const pieceDetails = parseIntervalDetails(sub.interval_details);
                        const expandedKey = `template-submission-${sub.id}`;
                        const isExpanded = !!expandedRows[expandedKey];
                        return (
                          <React.Fragment key={sub.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{idx + 1}</td>
                              <td className="px-4 py-2">{sub.athlete_name}</td>
                              <td className="px-4 py-2 text-xs">{sub.boat_class}</td>
                              <td className="px-4 py-2 font-mono">{formatTime(sub.total_time_seconds)}</td>
                              <td className="px-4 py-2 font-mono">{formatTime(sub.avg_split_seconds)}</td>
                              <td className="px-4 py-2">
                                {sub.gms_percentage != null ? (
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    sub.gms_percentage >= 90 ? 'bg-green-100 text-green-800' :
                                    sub.gms_percentage >= 80 ? 'bg-blue-100 text-blue-800' :
                                    sub.gms_percentage >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {sub.gms_percentage}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-2">{sub.avg_stroke_rate || '-'}</td>
                              <PrognosticTableCells result={sub} />
                              <td className="px-4 py-2">
                                {pieceDetails.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedRows(prev => ({ ...prev, [expandedKey]: !prev[expandedKey] }))}
                                    className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                                  >
                                    {isExpanded ? 'Hide' : `Show ${pieceDetails.length}`}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && pieceDetails.length > 0 && (
                              <tr className="bg-gray-50">
                                <td colSpan={12} className="px-4 py-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {pieceDetails.map((piece, pieceIdx) => (
                                      <div key={`${sub.id}-piece-${pieceIdx}`} className="rounded border bg-white px-3 py-2 text-xs">
                                        <p className="font-semibold text-gray-700 mb-1">Piece {pieceIdx + 1}</p>
                                        <p>Time: {piece.time_seconds ? formatTime(piece.time_seconds) : '-'}</p>
                                        <p>Distance: {piece.distance_meters || '-'}m</p>
                                        <p>SR: {piece.stroke_rate || '-'}</p>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pending Athletes */}
              {templateReport.pending.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">
                    Pending ({templateReport.pending.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {templateReport.pending.map(athlete => (
                      <span
                        key={athlete.id}
                        className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm"
                      >
                        {athlete.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-gray-200 pb-2">
            <h2 className="text-lg font-semibold text-theme-primary">Saved Reports</h2>
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No saved reports yet. Create reports from template submissions or athlete results.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {reports.map(report => (
                <div
                  key={report.id}
                  className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all hover:shadow-md ${
                    selectedReport?.id === report.id ? 'ring-2 ring-theme-primary' : ''
                  }`}
                  onClick={() => fetchReportDetails(report.id)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{report.title}</h3>
                      <p className="text-sm text-gray-500">
                        {report.result_count || 0} results • Created {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      report.shared_with_team ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {report.shared_with_team ? 'Shared' : 'Private'}
                    </span>
                  </div>
                  {report.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{report.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Report Detail */}
          {selectedReport && (
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedReport.title}</h2>
                  {selectedReport.description && (
                    <p className="text-gray-500">{selectedReport.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleShareReport(selectedReport.id, !selectedReport.shared_with_team)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${
                      selectedReport.shared_with_team
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    {selectedReport.shared_with_team ? 'Unshare' : 'Share with Team'}
                  </button>
                  <button
                    onClick={() => handleDeleteReport(selectedReport.id)}
                    className="px-3 py-1.5 text-sm bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {formatEnvironmentalConditions(selectedReport).length > 0 && (
                <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 text-sm text-teal-900">
                  <span className="font-semibold">Environmental Snapshot: </span>
                  {formatEnvironmentalConditions(selectedReport).join(' • ')}
                </div>
              )}

              {/* Results Table */}
              {selectedReport.results && selectedReport.results.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Rank</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Athlete</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Time</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Split</th>
                        {selectedReport.gmsTime && (
                          <th className="px-4 py-2 text-left font-medium text-gray-600">CST%</th>
                        )}
                        {PROGNOSTIC_HEADERS.map((h) => (
                          <th key={h.key} className="px-4 py-2 text-right font-medium text-gray-600">{h.label}</th>
                        ))}
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Date</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600">Pieces</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedReport.results.map((result, idx) => {
                        const pieceDetails = parseIntervalDetails(result.interval_details);
                        const expandedKey = `saved-report-result-${result.id}`;
                        const isExpanded = !!expandedRows[expandedKey];
                        return (
                          <React.Fragment key={result.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{idx + 1}</td>
                              <td className="px-4 py-2">{result.athlete_name}</td>
                              <td className="px-4 py-2 font-mono">{formatTime(result.total_time_seconds)}</td>
                              <td className="px-4 py-2 font-mono">{formatTime(result.avg_split_seconds)}</td>
                              {selectedReport.gmsTime && (
                                <td className="px-4 py-2">
                                  {result.gms_percentage != null ? (
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      result.gms_percentage >= 90 ? 'bg-green-100 text-green-800' :
                                      result.gms_percentage >= 80 ? 'bg-blue-100 text-blue-800' :
                                      result.gms_percentage >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {result.gms_percentage}%
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              )}
                              <PrognosticTableCells result={result} />
                              <td className="px-4 py-2 text-gray-500">{result.result_date}</td>
                              <td className="px-4 py-2">
                                {pieceDetails.length > 0 ? (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedRows(prev => ({ ...prev, [expandedKey]: !prev[expandedKey] }))}
                                    className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                                  >
                                    {isExpanded ? 'Hide' : `Show ${pieceDetails.length}`}
                                  </button>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && pieceDetails.length > 0 && (
                              <tr className="bg-gray-50">
                                <td colSpan={(selectedReport.gmsTime ? 7 : 6) + 4} className="px-4 py-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {pieceDetails.map((piece, pieceIdx) => (
                                      <div key={`${result.id}-piece-${pieceIdx}`} className="rounded border bg-white px-3 py-2 text-xs">
                                        <p className="font-semibold text-gray-700 mb-1">Piece {pieceIdx + 1}</p>
                                        <p>Time: {piece.time_seconds ? formatTime(piece.time_seconds) : '-'}</p>
                                        <p>Distance: {piece.distance_meters || '-'}m</p>
                                        <p>SR: {piece.stroke_rate || '-'}</p>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Template Modal */}
      <MobileModal
        isOpen={showTemplateForm}
        onClose={() => {
          setShowTemplateForm(false);
          resetTemplateForm();
        }}
        title="Create Result Template"
      >
        <form onSubmit={handleCreateTemplate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Spring 2k Test"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Instructions for athletes..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
            />
          </div>

          <div className="border rounded-lg p-3 bg-teal-50/50">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-teal-900">On-Water Conditions (optional)</p>
              <button
                type="button"
                onClick={fillFromLiveConditions}
                disabled={fillingConditions}
                className="text-xs px-3 py-1.5 rounded-lg border border-teal-700 text-teal-800 hover:bg-teal-100 disabled:opacity-50"
              >
                {fillingConditions ? 'Loading…' : 'Fill from live conditions'}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-2">
              <label className="flex-1 text-xs text-gray-600">
                Or fill from a specific date and time
                <input
                  type="datetime-local"
                  value={conditionsAtLocal}
                  onChange={(e) => setConditionsAtLocal(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                />
              </label>
              <button
                type="button"
                onClick={fillFromConditionsAt}
                disabled={fillingConditions}
                className="text-xs px-3 py-1.5 rounded-lg border border-teal-700 text-teal-800 hover:bg-teal-100 disabled:opacity-50 whitespace-nowrap"
              >
                {fillingConditions ? 'Loading…' : 'Fill from this time'}
              </button>
            </div>
            {conditionsFillMessage && (
              <p className="text-xs text-teal-800 mb-2">{conditionsFillMessage}</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Wind Speed (kts)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={templateWindSpeed}
                  onChange={(e) => setTemplateWindSpeed(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Wind Direction</label>
                <input
                  type="text"
                  value={templateWindDirection}
                  onChange={(e) => setTemplateWindDirection(e.target.value)}
                  placeholder="e.g., SW, Headwind, Tailwind"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Flow Rate (cfs)</label>
                <input
                  type="number"
                  step="0.1"
                  value={templateFlowRate}
                  onChange={(e) => setTemplateFlowRate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Water Temperature (°C)</label>
                <input
                  type="number"
                  step="0.1"
                  value={templateWaterTemperature}
                  onChange={(e) => setTemplateWaterTemperature(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Time of Day</label>
                <input
                  type="text"
                  value={templateTimeOfDay}
                  onChange={(e) => setTemplateTimeOfDay(e.target.value)}
                  placeholder="e.g., Dawn, Midday, Evening"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Piece Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Piece Type *</label>
            <div className="grid grid-cols-2 gap-3">
              {PIECE_TYPES.map(pt => (
                <button
                  key={pt.value}
                  type="button"
                  onClick={() => setTemplatePieceType(pt.value)}
                  className={`p-3 text-left border rounded-lg transition-all ${
                    templatePieceType === pt.value
                      ? 'border-theme-primary bg-theme-primary/5 ring-2 ring-theme-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm">{pt.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{pt.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Distance or Time input based on piece type - hidden for variable intervals */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {templateIntervalMode !== 'variable' && (
              templatePieceType === 'distance' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Distance (meters) *</label>
                  <select
                    value={templateDistance}
                    onChange={(e) => {
                      setTemplateDistance(e.target.value);
                      if (e.target.value !== 'custom') setTemplateDistanceCustom('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                    required={templatePieceType === 'distance' && templateIntervalMode !== 'variable'}
                  >
                    <option value="">Select...</option>
                    {COMMON_DISTANCES.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                    <option value="custom">Custom...</option>
                  </select>
                  {templateDistance === 'custom' && (
                    <input
                      type="number"
                      placeholder="Enter meters"
                      value={templateDistanceCustom}
                      onChange={(e) => setTemplateDistanceCustom(e.target.value)}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                  <select
                    value={templateTimeSeconds}
                    onChange={(e) => {
                      setTemplateTimeSeconds(e.target.value);
                      if (e.target.value !== 'custom') setTemplateTimeCustom('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                    required={templatePieceType === 'time' && templateIntervalMode !== 'variable'}
                  >
                    <option value="">Select...</option>
                    {COMMON_DURATIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                    <option value="custom">Custom...</option>
                  </select>
                  {templateTimeSeconds === 'custom' && (
                    <input
                      type="number"
                      placeholder="Enter seconds"
                      value={templateTimeCustom}
                      onChange={(e) => setTemplateTimeCustom(e.target.value)}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  )}
                </div>
              )
            )}

            <div className={templateIntervalMode === 'variable' ? 'col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">Send To Team</label>
              <select
                value={templateTeam}
                onChange={(e) => setTemplateTeam(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              >
                <option value="">All Teams</option>
                {availableTeams.map(team => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {templateTeam ? `Only ${templateTeam} athletes will see this` : 'All athletes in club will see this'}
              </p>
            </div>
          </div>

          {/* Interval Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workout Structure</label>
            <select
              value={templateIntervalMode}
              onChange={(e) => setTemplateIntervalMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
            >
              {INTERVAL_TYPES.map(it => (
                <option key={it.value} value={it.value}>{it.label}</option>
              ))}
            </select>
          </div>

          {/* Uniform Interval Settings */}
          {templateIntervalMode === 'fixed' && (
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Intervals</label>
                  <input
                    type="number"
                    value={templateIntervalCount}
                    onChange={(e) => setTemplateIntervalCount(e.target.value)}
                    min="1"
                    max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                {templatePieceType === 'distance' ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Distance (m)</label>
                    <select
                      value={templateIntervalDistance}
                      onChange={(e) => {
                        setTemplateIntervalDistance(e.target.value);
                        if (e.target.value !== 'custom') setTemplateIntervalDistanceCustom('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="250">250m</option>
                      <option value="500">500m</option>
                      <option value="750">750m</option>
                      <option value="1000">1000m</option>
                      <option value="1500">1500m</option>
                      <option value="2000">2000m</option>
                      <option value="custom">Custom...</option>
                    </select>
                    {templateIntervalDistance === 'custom' && (
                      <input
                        type="number"
                        placeholder="Enter meters"
                        value={templateIntervalDistanceCustom}
                        onChange={(e) => setTemplateIntervalDistanceCustom(e.target.value)}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                    <select
                      value={templateIntervalTime}
                      onChange={(e) => {
                        setTemplateIntervalTime(e.target.value);
                        if (e.target.value !== 'custom') setTemplateIntervalTimeCustom('');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Select...</option>
                      <option value="60">1'</option>
                      <option value="120">2'</option>
                      <option value="180">3'</option>
                      <option value="240">4'</option>
                      <option value="300">5'</option>
                      <option value="420">7'</option>
                      <option value="600">10'</option>
                      <option value="custom">Custom...</option>
                    </select>
                    {templateIntervalTime === 'custom' && (
                      <input
                        type="number"
                        placeholder="Enter seconds"
                        value={templateIntervalTimeCustom}
                        onChange={(e) => setTemplateIntervalTimeCustom(e.target.value)}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rest (M:SS)</label>
                  <input
                    type="text"
                    value={templateIntervalRest}
                    onChange={(e) => setTemplateIntervalRest(e.target.value)}
                    placeholder="1:00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Preview: {templateIntervalCount || '?'}x
                {templatePieceType === 'distance'
                  ? `${templateIntervalDistance === 'custom' ? (templateIntervalDistanceCustom || '?') : (templateIntervalDistance || '?')}m`
                  : `${Math.floor(((templateIntervalTime === 'custom' ? templateIntervalTimeCustom : templateIntervalTime) || 0) / 60)}'`}
                {templateIntervalRest && ` / ${templateIntervalRest} rest`}
              </p>
            </div>
          )}

          {/* Variable Intervals Builder */}
          {templateIntervalMode === 'variable' && (
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <p className="text-xs text-gray-600 mb-2">
                {intervalInputMode === 'distance' 
                  ? 'Define each interval distance (athletes will submit their time for each)'
                  : 'Define each interval duration (athletes will submit distance covered)'}
              </p>
              {templateVariableIntervals.map((interval, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-6">{idx + 1}.</span>
                  {intervalInputMode === 'time' ? (
                    <>
                      <input
                        type="number"
                        value={interval.time_seconds}
                        onChange={(e) => updateVariableInterval(idx, 'time_seconds', e.target.value)}
                        placeholder="Seconds"
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      <span className="text-xs text-gray-500">sec</span>
                    </>
                  ) : (
                    <>
                      <input
                        type="number"
                        value={interval.distance_meters}
                        onChange={(e) => updateVariableInterval(idx, 'distance_meters', e.target.value)}
                        placeholder="Meters"
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      <span className="text-xs text-gray-500">m</span>
                    </>
                  )}
                  <input
                    type="text"
                    value={interval.rest_seconds}
                    onChange={(e) => updateVariableInterval(idx, 'rest_seconds', e.target.value)}
                    placeholder="Rest"
                    className="w-16 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeVariableInterval(idx)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    disabled={templateVariableIntervals.length <= 1}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addVariableInterval}
                className="text-sm text-theme-primary hover:underline"
              >
                + Add Interval
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Preview: {templateVariableIntervals
                  .filter(i => intervalInputMode === 'time' ? i.time_seconds : i.distance_meters)
                  .map(i => intervalInputMode === 'time'
                    ? `${Math.floor(i.time_seconds / 60)}'${i.time_seconds % 60 > 0 ? `${i.time_seconds % 60}"` : ''}`
                    : `${i.distance_meters}m`)
                  .join(' / ') || 'Enter intervals above'}
              </p>
            </div>
          )}

          <div className="p-3 rounded border bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Intended Zone Minutes (optional)</label>
              {!zoneLoggingEnabled && (
                <span className="text-xs text-gray-500">Disabled for your team</span>
              )}
            </div>
            {!zoneLoggingEnabled && (
              <p className="text-xs text-gray-500 mb-2">
                Zone logging is OFF for your team. You can still create templates without zone targets.
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {zoneDefinitions.map(zone => (
                <div key={`template-zone-${zone.key}`}>
                  <label className="block text-xs text-gray-600 mb-1">{zone.label}</label>
                  <input
                    type="number"
                    min="0"
                    disabled={!zoneLoggingEnabled}
                    value={templateIntendedZones[zone.key] || ''}
                    onChange={(e) => setTemplateIntendedZones(prev => ({ ...prev, [zone.key]: e.target.value }))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                  />
                </div>
              ))}
            </div>
          </div>

          {targetTimesEnabled && (
          <GoalPercentPicker
            value={templateGoalPercent}
            onChange={setTemplateGoalPercent}
            hint="Pick a stroke rate to fill the chart percent, then keep it or type a different %. After results are submitted, % of goal uses this percent. If the goal is 90% and an athlete goes 81% of 2k speed, they hit 90% of goal."
          />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Result Type</label>
              <select
                value={templateResultType}
                onChange={(e) => setTemplateResultType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              >
                {RESULT_TYPES.map(rt => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="datetime-local"
                value={templateDueDate}
                onChange={(e) => setTemplateDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={templateSendNotification}
                onChange={(e) => setTemplateSendNotification(e.target.checked)}
                className="rounded border-gray-300 text-theme-primary focus:ring-theme-primary"
              />
              <span className="text-sm text-gray-700">Send email notification to athletes</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowTemplateForm(false);
                resetTemplateForm();
              }}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-theme-primary text-white rounded-lg hover:opacity-90"
            >
              Create Template
            </button>
          </div>
        </form>
      </MobileModal>

      </div>
  );
}

